/*
 * Poundsale wishlist (no app needed).
 *
 * Saved product handles live in the visitor's browser (localStorage key "ps-wishlist-v1"), newest first.
 *   [data-ps-wish][data-handle]   heart buttons (snippets/ps-wishlist-button.liquid) - toggle + aria-pressed sync
 *   [data-ps-wish-count]          header counters - show the number of saved products (hidden at 0)
 *   <ps-wishlist>                 wishlist page (templates/collection.wishlist.json -> sections/ps-wishlist.liquid)
 *                                 renders each saved product through /products/<handle>?view=ps-card
 *                                 (templates/product.ps-card.liquid = snippets/ps-product-card.liquid, ADD TO CART included)
 *   product pages                 a "Save to wishlist" button is added after the add-to-cart form when the template has none.
 *   every .product-card           (snippets/card-product*.liquid: collections, search, recommendations, ...) gets a heart in
 *                                 its image corner, injected here because those snippets are not edited. Cards added later
 *                                 (infinite scroll, filters, section re-renders) are picked up by the MutationObserver.
 *   quick view                    the opened quick view gets the same "Save to wishlist" row as the product page.
 *   toast                         saving / removing from any heart shows "Added to wishlist · View" / "Removed from wishlist".
 *
 * Public API: window.PsWishlist { all(), has(handle), toggle(handle), remove(handle), clear(), count(), onChange(fn) }
 * Event: document "ps-wishlist:change" { detail: { handle, saved, items } }
 */
(() => {
  if (window.PsWishlist) return;

  const KEY = 'ps-wishlist-v1';
  const MAX = 100;
  const listeners = new Set();
  // Shopify product handles only; anything else stored in localStorage is ignored
  const isHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value);

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(value) ? value.filter(isHandle).slice(0, MAX) : [];
    } catch (error) {
      return [];
    }
  };

  let items = read();

  const write = (next) => {
    items = next;
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (error) {
      /* private mode / blocked storage: keep the list in memory for this page view */
    }
  };

  const emit = (handle, saved) => {
    sync();
    listeners.forEach((fn) => fn({ handle, saved, items: items.slice() }));
    document.dispatchEvent(new CustomEvent('ps-wishlist:change', { detail: { handle, saved, items: items.slice() } }));
  };

  const api = {
    all: () => items.slice(),
    count: () => items.length,
    has: (handle) => items.includes(handle),
    toggle(handle) {
      if (!isHandle(handle)) return false;
      const saved = !items.includes(handle);
      write(saved ? [handle, ...items].slice(0, MAX) : items.filter((item) => item !== handle));
      emit(handle, saved);
      return saved;
    },
    remove(handle) {
      if (!items.includes(handle)) return;
      write(items.filter((item) => item !== handle));
      emit(handle, false);
    },
    clear() {
      const old = items.slice();
      write([]);
      old.forEach((handle) => emit(handle, false));
    },
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
  window.PsWishlist = api;

  /* ---------------------------------------------------------------- */
  /* Buttons + counters                                                 */
  /* ---------------------------------------------------------------- */
  function sync() {
    document.querySelectorAll('[data-ps-wish]').forEach((button) => {
      const saved = items.includes(button.dataset.handle);
      if (button.getAttribute('aria-pressed') !== String(saved)) button.setAttribute('aria-pressed', String(saved));
      const title = saved ? 'Remove from wishlist' : 'Add to wishlist';
      if (button.title !== title) button.title = title;
      const label = button.querySelector('[data-ps-wish-label]');
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Save to wishlist';
    });
    document.querySelectorAll('[data-ps-wish-count]').forEach((badge) => {
      badge.textContent = items.length > 99 ? '99+' : String(items.length);
      badge.hidden = items.length === 0;
    });
  }

  /* ---------------------------------------------------------------- */
  /* Toast: "Added to wishlist · View"                                   */
  /* ---------------------------------------------------------------- */
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const wishlistUrl = () => root() + 'collections/all?view=wishlist';
  let toast = null;
  let toastTimer = 0;

  function showToast(saved) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ps-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = '';
    const text = document.createElement('span');
    text.className = 'ps-toast__text';
    text.textContent = saved ? 'Added to wishlist' : 'Removed from wishlist';
    toast.appendChild(text);
    if (!/[?&]view=wishlist\b/.test(window.location.search)) {
      const sep = document.createElement('span');
      sep.className = 'ps-toast__sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '\u00b7';
      const link = document.createElement('a');
      link.className = 'ps-toast__link';
      link.href = wishlistUrl();
      link.textContent = 'View';
      toast.append(sep, link);
    }
    // restart the enter transition when a toast is already showing
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast && toast.classList.remove('is-visible'), 3500);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ps-wish]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const saved = api.toggle(button.dataset.handle);
    button.classList.remove('is-pop');
    void button.offsetWidth;
    button.classList.add('is-pop');
    showToast(saved);
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    items = read();
    sync();
    listeners.forEach((fn) => fn({ handle: null, saved: null, items: items.slice() }));
  });

  /* ---------------------------------------------------------------- */
  /* Heart markup (built with DOM calls, no innerHTML)                  */
  /* ---------------------------------------------------------------- */
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const heartSvg = (size) => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M12 20.3S3.5 15 3.5 8.9A4.6 4.6 0 0 1 12 6.5a4.6 4.6 0 0 1 8.5 2.4c0 6.1-8.5 11.4-8.5 11.4z');
    svg.appendChild(path);
    return svg;
  };

  const handleFromHref = (href) => {
    if (!href) return null;
    try {
      const match = new URL(href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      if (!match) return null;
      const handle = decodeURIComponent(match[1]);
      return isHandle(handle) ? handle : null;
    } catch (error) {
      return null;
    }
  };

  /* ---------------------------------------------------------------- */
  /* Every product card: heart in the image corner                      */
  /* ---------------------------------------------------------------- */
  function ensureCardButtons() {
    document.querySelectorAll('.product-card:not([data-ps-wish-ready])').forEach((card) => {
      if (card.closest('.ps-pcard')) return;
      const media = card.querySelector('.product-card__image-wrapper') || card;
      const link = media.querySelector('a[href*="/products/"]') || card.querySelector('a[href*="/products/"]');
      const handle = link && handleFromHref(link.getAttribute('href'));
      if (!handle) return;
      card.setAttribute('data-ps-wish-ready', '');
      if (media.querySelector('[data-ps-wish]')) return;
      const titleEl = card.querySelector('.product-card__title');
      const title = (titleEl && titleEl.textContent.trim().replace(/\s+/g, ' ')) || 'this product';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ps-wish ps-wish--card';
      button.setAttribute('data-ps-wish', '');
      button.dataset.handle = handle;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Add ' + title + ' to wishlist');
      button.title = 'Add to wishlist';
      button.appendChild(heartSvg(17));
      media.appendChild(button);
    });
  }

  /* ---------------------------------------------------------------- */
  /* Product page + quick view: "Save to wishlist" row after the form   */
  /* ---------------------------------------------------------------- */
  function buildProductRow(handle) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ps-wish-row';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ps-wish ps-wish--product';
    button.setAttribute('data-ps-wish', '');
    button.dataset.handle = handle;
    button.setAttribute('aria-pressed', 'false');
    button.appendChild(heartSvg(20));
    const label = document.createElement('span');
    label.setAttribute('data-ps-wish-label', '');
    label.textContent = 'Save to wishlist';
    button.appendChild(label);
    const link = document.createElement('a');
    link.className = 'ps-wish-row__link';
    link.href = wishlistUrl();
    link.textContent = 'View wishlist';
    wrapper.append(button, link);
    return wrapper;
  }

  function ensureProductButton() {
    if (!document.body.classList.contains('product-template')) return;
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = decodeURIComponent(match[1]);
    if (!isHandle(handle)) return;
    const host = document.querySelector('main .product-form__buttons');
    if (!host || document.querySelector('main .ps-wish--product')) return;
    const form = host.closest('form') || host;
    form.insertAdjacentElement('afterend', buildProductRow(handle));
    sync();
  }

  function ensureQuickViewButtons() {
    document.querySelectorAll('quick-view-modal').forEach((modal) => {
      if (modal.querySelector('.ps-wish--product')) return;
      const host = modal.querySelector('.product-form__buttons');
      if (!host) return;
      const handle = handleFromHref(modal.getAttribute('data-product-url'));
      if (!handle) return;
      const form = host.closest('form') || host;
      form.insertAdjacentElement('afterend', buildProductRow(handle));
      sync();
    });
  }

  let queued = false;
  const onMutate = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      ensureCardButtons();
      ensureProductButton();
      ensureQuickViewButtons();
      sync();
    });
  };

  const start = () => {
    ensureCardButtons();
    ensureProductButton();
    ensureQuickViewButtons();
    sync();
    new MutationObserver(onMutate).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* ---------------------------------------------------------------- */
  /* Wishlist page                                                       */
  /* ---------------------------------------------------------------- */
  if (!customElements.get('ps-wishlist')) {
    customElements.define(
      'ps-wishlist',
      class PsWishlistPage extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wl-grid]');
          this.empty = this.querySelector('[data-wl-empty]');
          this.loading = this.querySelector('[data-wl-loading]');
          this.countEl = this.querySelector('[data-wl-count]');
          this.clearBtn = this.querySelector('[data-wl-clear]');
          this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

          if (this.clearBtn) {
            this.clearBtn.addEventListener('click', () => {
              if (window.confirm(this.dataset.confirm || 'Remove every product from your wishlist?')) api.clear();
            });
          }
          this.off = api.onChange((detail) => this.onChange(detail));
          document.title = (this.dataset.title || 'My wishlist') + ' – ' + (document.title.split('–').pop() || '').trim();
          this.render();
        }

        disconnectedCallback() {
          if (this.off) this.off();
        }

        async render() {
          const handles = api.all();
          this.grid.innerHTML = '';
          this.update();
          if (!handles.length) return;
          this.loading.hidden = false;

          const results = await Promise.all(
            handles.map(async (handle) => {
              try {
                const response = await fetch(this.root + 'products/' + encodeURIComponent(handle) + '?view=ps-card', {
                  headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (response.status === 404) return { handle, gone: true };
                if (!response.ok) return { handle, failed: true };
                return { handle, html: await response.text() };
              } catch (error) {
                return { handle, failed: true };
              }
            })
          );

          results.forEach((result) => {
            if (result.gone) return api.remove(result.handle);
            if (!result.html) return;
            const card = new DOMParser().parseFromString(result.html, 'text/html').querySelector('.ps-pcard');
            if (!card) return;
            const li = document.createElement('li');
            li.className = 'ps-wl__item';
            li.dataset.handle = result.handle;
            li.innerHTML = card.outerHTML;
            this.grid.appendChild(li);
          });
          this.loading.hidden = true;
          this.update();
          sync();
        }

        update() {
          const total = api.count();
          this.empty.hidden = total > 0;
          if (this.clearBtn) this.clearBtn.hidden = total === 0;
          if (this.countEl) this.countEl.textContent = total ? '(' + total + ')' : '';
          if (!total) this.loading.hidden = true;
        }

        onChange({ handle, saved }) {
          if (saved === false && handle) {
            const li = this.grid.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
            if (li) {
              li.classList.add('is-leaving');
              setTimeout(() => {
                li.remove();
                this.update();
              }, 200);
            }
          } else if (saved === true || saved === null) {
            this.render();
          }
          this.update();
        }
      }
    );
  }
})();
